import { err, extractErrorDetails, ok } from '@src/services/common/Result';
import { authenticatedFetch } from '@src/utility/authenticatedFetch';

import type { ClientSceneDetectionParams } from './runClientVideoAnalysis';
import type { FaceTracksJson } from '@arborstoryorg/video-analysis';
import type { Result } from '@src/services/common/Result';

/** Vercel caps function request bodies at 4.5 MB; leave headroom. */
export const MAX_CLIENT_ANALYSIS_BODY_BYTES = 4 * 1024 * 1024;

export type ClientVideoAnalysisPayload = {
  /** `scenes.csv` in the pySceneDetect Lambda's format. */
  scenesCsv: string;
  /** `face-tracks.json` in the faceDetect Lambda's format. */
  faceTracks: FaceTracksJson;
  meta: {
    assetVersion: string;
    backend: 'webgpu' | 'wasm';
    elapsedMs: number;
    scenes: ClientSceneDetectionParams;
  };
};

/**
 * Saves the browser's scene and face detection results for a story
 * (PLAT-12104 / PLAT-12105). arbor-v1 writes them where the detection Lambdas
 * write theirs, so every existing reader picks them up unchanged.
 */
export class ClientVideoAnalysisService {
  constructor(
    private readonly apiUrl: string,
    private readonly getAuthToken: () => Promise<string | undefined>,
  ) {}

  async saveResults(
    storyId: string,
    payload: ClientVideoAnalysisPayload,
    signal?: AbortSignal,
  ): Promise<Result<{ payloadBytes: number }>> {
    const body = JSON.stringify(payload);
    const payloadBytes = new TextEncoder().encode(body).byteLength;
    if (payloadBytes > MAX_CLIENT_ANALYSIS_BODY_BYTES) {
      return err('PRECONDITION', 'Analysis result too large to upload', {
        payloadBytes,
      });
    }
    try {
      const response = await authenticatedFetch(
        `${this.apiUrl}/api/story/${storyId}/client-video-analysis`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          signal,
        },
        this.getAuthToken,
      );
      if (!response.ok) {
        return err(
          response.status === 401 ? 'UNAUTHENTICATED' : 'EXTERNAL',
          `Saving analysis failed with HTTP ${response.status}`,
        );
      }
      return ok({ payloadBytes });
    } catch (error) {
      return err(
        'EXTERNAL',
        'Saving analysis failed',
        extractErrorDetails(error),
      );
    }
  }
}
